<template>
	<div
		id="factionsView"
		:class="{ animate: animateView }"
		:style="{ 'animation-delay': animationDelay }"
		class="content-container"
	>
		<div class="source-tag factions-source" v-decrypt="{ after: '#factionsView', delay: 0.1 }">
			// Records retrieved from Order Xenoglossia's Baronic Record and Chamber of Arms //
		</div>

		<!-- One full-width panel: the overview, or a dossier -->
		<!-- Right: Faction Detail -->
		<section id="faction-detail" :class="{ animate: animate }" class="section-container">
			<div style="height: 52px; overflow: hidden">
				<div class="section-header clipped-medium-backward-events-logs">
					<img src="/icons/squad.svg" />
					<h1>BARONIC RECORDS</h1>
				</div>
				<div class="rhombus-back">&nbsp;</div>
			</div>
			<div class="section-content-container extra-margins">
				<div ref="dossier" class="faction-dossier" v-if="selectedFaction">
					<button class="dossier-back" @click="selectedFaction = null">◀ ALL FACTIONS</button>
					<div class="dossier-top">
						<div class="dossier-left">
							<div class="dossier-header">
								<h1>
									{{ selectedFaction.type
									}}<template v-if="selectedFaction.subtype">
										// {{ selectedFaction.subtype }}</template
									>
								</h1>
								<h2>{{ selectedFaction.name }}</h2>
								<span v-if="selectedFaction.politicalAlignment" class="dossier-title">{{
									selectedFaction.politicalAlignment
								}}</span>
							</div>

							<!-- Status Tracker -->
							<div v-if="!party || selectedFaction.id !== party.id" class="stat-section">
								<span class="stat-label">DISPOSITION</span>
								<FactionStatus :value="partyStanding(selectedFaction)" />
							</div>

							<!-- Motto -->
							<blockquote v-if="selectedFaction.motto" class="dossier-quote">
								{{ selectedFaction.motto }}
							</blockquote>

							<!-- Faction Info Grid -->
							<div class="faction-info-grid">
								<div class="info-field">
									<span class="stat-label">DAWNLINE SEAT</span>
									<span class="info-value" :class="{ 'no-seat': !selectedFaction.seat }">{{
										selectedFaction.seat || noSeat
									}}</span>
								</div>
								<div v-if="selectedFaction.homeworld || selectedFaction.demonym" class="info-field">
									<template v-if="selectedFaction.homeworld">
										<span class="stat-label">HOMEWORLD</span>
										<span class="info-value">{{ selectedFaction.homeworld }}</span>
									</template>
									<template v-if="selectedFaction.demonym">
										<span
											class="stat-label"
											:style="selectedFaction.homeworld ? 'margin-top: 0.5em' : ''"
											>DEMONYM</span
										>
										<span class="info-value">{{ selectedFaction.demonym }}</span>
									</template>
								</div>
								<div
									v-if="selectedFaction.descriptors && selectedFaction.descriptors.length"
									class="info-field"
								>
									<span class="stat-label">DESCRIPTORS</span>
									<span class="info-value">{{ selectedFaction.descriptors.join(", ") }}</span>
								</div>
								<div
									v-if="selectedFaction.desires && selectedFaction.desires.length"
									class="info-field"
								>
									<span class="stat-label">DESIRES</span>
									<span class="info-value">{{ selectedFaction.desires.join(", ") }}</span>
								</div>
								<div
									v-if="selectedFaction.fears && selectedFaction.fears.length"
									class="info-field"
								>
									<span class="stat-label">FEARS</span>
									<span class="info-value">{{ selectedFaction.fears.join(", ") }}</span>
								</div>
							</div>

							<!-- Stat card on the left, description on the right -->
							<div class="profile-split">
								<!-- Faction profile: Stars Without Numbers stats, tags and goal -->
								<div v-if="selectedFaction.swn || selectedFaction.tags.length" class="profile">
									<div v-if="selectedFaction.swn" class="profile-attrs">
										<div
											v-for="attr in ['force', 'cunning', 'wealth']"
											:key="attr"
											class="swn-attr"
											:class="attr"
										>
											<span class="swn-attr-value">{{ selectedFaction.swn[attr] }}</span>
											<span class="swn-attr-name">{{ attr }}</span>
											<!-- Rating out of the SWN maximum of 8 -->
											<span class="attr-boxes">
												<span
													v-for="n in 8"
													:key="n"
													class="attr-box"
													:class="{ filled: n <= selectedFaction.swn[attr] }"
												></span>
											</span>
										</div>
									</div>

									<div v-if="selectedFaction.swn" class="profile-row">
										<span class="profile-label">HP</span>
										<span class="hp-track"
											><span
												class="hp-fill"
												:style="{
													width: (100 * selectedFaction.swn.hp) / selectedFaction.swn.maxHp + '%',
												}"
											></span
										></span>
										<span class="profile-value"
											>{{ selectedFaction.swn.hp
											}}<span class="profile-of">/{{ selectedFaction.swn.maxHp }}</span></span
										>
									</div>
									<div v-if="selectedFaction.swn" class="profile-row">
										<span class="profile-label">CRED</span>
										<span class="profile-value">{{ selectedFaction.swn.cred }}</span>
										<span class="profile-label profile-label-inline">XP</span>
										<span class="profile-value">{{ selectedFaction.swn.xp }}</span>
									</div>

									<div
										v-if="selectedFaction.swn && selectedFaction.swn.goal"
										class="profile-row profile-top"
									>
										<span class="profile-label">GOAL</span>
										<div class="profile-goal">
											<div class="goal-head">
												<span class="goal-name">{{ selectedFaction.swn.goal }}</span>
												<span v-if="selectedFaction.swn.goalTarget" class="goal-target"
													>// {{ selectedFaction.swn.goalTarget }}</span
												>
											</div>
											<div v-if="selectedFaction.swn.goalDifficulty" class="goal-progress">
												<span
													v-for="n in selectedFaction.swn.goalDifficulty"
													:key="n"
													class="goal-box"
													:class="{ filled: n <= selectedFaction.swn.goalProgress }"
												></span>
												<span class="profile-of"
													>{{ selectedFaction.swn.goalProgress }}/{{
														selectedFaction.swn.goalDifficulty
													}}</span
												>
											</div>
											<p v-if="selectedFaction.swn.goalRule" class="profile-rule">
												{{ selectedFaction.swn.goalRule }}
											</p>
										</div>
									</div>

									<div v-if="selectedFaction.tags.length" class="profile-row profile-top">
										<span class="profile-label">TAGS</span>
										<div class="profile-tags">
											<div
												v-for="(tag, i) in selectedFaction.tags"
												:key="'t-' + i"
												class="profile-tag"
											>
												<span class="tag-name">{{ tag }}</span>
												<p v-if="tagRule(tag)" class="profile-rule">{{ tagRule(tag) }}</p>
											</div>
										</div>
									</div>
								</div>

								<!-- Notes -->
								<vue-markdown-it
									v-if="selectedFaction.notes"
									:source="selectedFaction.notes"
									preset="default"
									:options="{ html: true }"
									class="faction-note"
								/>
								<p v-else class="faction-missing">[NO RECORD ON FILE]</p>
								<!-- How this faction stands with the others; click one to open its dossier -->
								<div class="dossier-relations">
									<span class="stat-label">RELATIONS</span>
									<template v-for="side in ['allied', 'hostile']" :key="side">
										<div v-if="dossierRelations[side].length" class="relation-group">
											<span class="relation-side" :class="side">{{ side }}</span>
											<div class="standing-chips">
												<button
													v-for="r in dossierRelations[side]"
													:key="r.faction.id"
													class="faction-chip"
													:class="side"
													@click="selectedFaction = r.faction"
												>
													<img v-if="r.faction.emblem" :src="r.faction.emblem" />
													<span v-else class="chip-emblem-empty"></span>
													{{ r.faction.name }}
													<span class="relation-value" :class="side">{{
														r.value > 0 ? "+" + r.value : "−" + Math.abs(r.value)
													}}</span>
												</button>
											</div>
										</div>
									</template>
									<span
										v-if="!dossierRelations.allied.length && !dossierRelations.hostile.length"
										class="box-empty"
										>// NO RELATIONS ON RECORD //</span
									>
								</div>
							</div>

							<!-- Significant Details -->
							<div class="faction-details-grid">
								<div
									v-if="
										selectedFaction.significantObjects && selectedFaction.significantObjects.length
									"
									class="detail-field"
								>
									<span class="stat-label">SIGNIFICANT OBJECTS</span>
									<ul class="detail-list">
										<li v-for="(item, i) in selectedFaction.significantObjects" :key="'obj-' + i">
											{{ item }}
										</li>
									</ul>
								</div>
								<div
									v-if="
										selectedFaction.significantLocations &&
										selectedFaction.significantLocations.length
									"
									class="detail-field"
								>
									<span class="stat-label">SIGNIFICANT LOCATIONS</span>
									<ul class="detail-list">
										<li v-for="(item, i) in selectedFaction.significantLocations" :key="'loc-' + i">
											{{ item }}
										</li>
									</ul>
								</div>
								<div
									v-if="
										selectedFaction.significantIncidents &&
										selectedFaction.significantIncidents.length
									"
									class="detail-field"
								>
									<span class="stat-label">SIGNIFICANT INCIDENTS</span>
									<ul class="detail-list">
										<li v-for="(item, i) in selectedFaction.significantIncidents" :key="'inc-' + i">
											{{ item }}
										</li>
									</ul>
								</div>
								<div
									v-if="
										selectedFaction.interestingPeople && selectedFaction.interestingPeople.length
									"
									class="detail-field"
								>
									<span class="stat-label">INTERESTING PEOPLE</span>
									<ul class="detail-list">
										<li v-for="(item, i) in selectedFaction.interestingPeople" :key="'ppl-' + i">
											{{ item }}
										</li>
									</ul>
								</div>
							</div>
						</div>

						<div class="dossier-banner-wrapper">
							<img
								v-if="selectedFaction.banner"
								:src="selectedFaction.banner"
								class="dossier-banner"
								@error="e => (e.target.style.display = 'none')"
							/>
							<img
								v-else-if="selectedFaction.emblem"
								:src="selectedFaction.emblem"
								class="dossier-emblem"
							/>
						</div>
					</div>

					<!-- Holdings by world | Turn log (full width at bottom) -->
					<div class="assets-holdings-row">
						<div class="assets-box holdings-box">
							<div class="box-header">
								<span class="stat-label">HOLDINGS</span>
							</div>
							<div class="box-content holdings-content">
								<template v-if="selectedFaction.bases.length">
									<div class="holding-section">Bases of Influence</div>
									<div
										v-for="(base, i) in selectedFaction.bases"
										:key="'b-' + i"
										class="holding-row base-row"
									>
										<span class="holding-cat" :class="base.type.toLowerCase()">{{
											base.type.charAt(0)
										}}</span>
										<span class="holding-name"
											>{{ base.type }} Base<span v-if="base.homeworld" class="homeworld-tag"
												>Homeworld</span
											></span
										>
										<span class="holding-loc">{{ base.world }}</span>
										<span class="holding-stat">HP {{ base.hp }}/{{ base.maxHp }}</span>
										<span v-if="base.note" class="holding-note">{{ base.note }}</span>
									</div>
								</template>
								<template v-if="selectedFaction.assets.length">
									<div class="holding-section">Assets</div>
									<details
										v-for="(asset, i) in selectedFaction.assets"
										:key="'a-' + i"
										class="holding-row asset-row"
									>
										<summary>
											<span class="holding-cat" :class="asset.category">{{
												(asset.category || "?").charAt(0)
											}}</span>
											<span class="holding-name">{{ asset.name }}</span>
											<span class="holding-loc">{{ asset.world }}</span>
											<span class="holding-stat"
												><template v-if="asset.maxHp"
													>HP {{ asset.hp }}/{{ asset.maxHp }}</template
												></span
											>
											<span v-if="asset.note" class="holding-note">{{ asset.note }}</span>
										</summary>
										<div class="asset-detail">
											<!-- [Type] [Rating] [Cost] [Tags] -->
											<div class="asset-specs">
												<span v-if="asset.category" class="asset-tag" :class="asset.category">{{
													asset.category
												}}</span>
												<span v-if="asset.rating" class="asset-tag">Rating {{ asset.rating }}</span>
												<span v-if="asset.cost != null" class="asset-tag"
													>Cost {{ asset.cost }} Cred</span
												>
												<span v-if="asset.attack" class="asset-tag">Attack {{ asset.attack }}</span>
												<span v-if="asset.counter" class="asset-tag"
													>Counter {{ asset.counter }}</span
												>
											</div>
											<p v-if="asset.description">{{ asset.description }}</p>
										</div>
									</details>
								</template>
								<span
									v-if="!selectedFaction.bases.length && !selectedFaction.assets.length"
									class="box-empty"
									>// NO HOLDINGS ON RECORD //</span
								>
							</div>
						</div>
						<div class="assets-box turnlog-box">
							<div class="box-header">
								<span class="stat-label">TURN LOG</span>
							</div>
							<div class="box-content turnlog-content">
								<div v-for="(entry, i) in turnEntries" :key="'tl-' + i" class="turn-row">
									<div class="turn-head">
										<span class="turn-when">{{ entry.turn || "—" }}</span>
										<span class="turn-action">{{ entry.by ? "Defended" : entry.action }}</span>
										<span
											v-if="entry.outcome"
											class="turn-outcome"
											:class="/success/i.test(entry.outcome) ? 'success' : 'failure'"
											>{{ entry.outcome }}</span
										>
									</div>
									<!-- [Asset] attacks [Faction] on [World], defended by [Asset] -->
									<p v-if="entry.asset || entry.target" class="turn-line">
										<template v-if="entry.by"
											><button class="turn-faction" @click="selectedFaction = entry.by">
												{{ entry.by.name }}</button
											>'s
										</template>
										<template v-if="entry.asset"
											><span
												class="turn-asset"
												:class="entry.asset.category"
												v-tip="assetTip(entry.asset)"
												>{{ entry.asset.name }}</span
											>
											{{ entry.target ? " targets " : " " }}
										</template>
										<template v-if="entry.target">
											<button
												v-if="factionById(entry.target)"
												class="turn-faction"
												@click="selectedFaction = factionById(entry.target)"
											>
												{{ factionById(entry.target).name }}
											</button>
											<span v-else>{{ entry.target }}</span>
										</template>
										<template v-if="entry.world">
											on <span class="turn-world">{{ entry.world }}</span></template
										><template v-if="entry.defender"
											>, defended by
											<span
												class="turn-asset"
												:class="entry.defender.category"
												v-tip="assetTip(entry.defender)"
												>{{ entry.defender.name }}</span
											></template
										>.
									</p>
									<p v-if="entry.result" class="turn-result">{{ entry.result }}</p>
								</div>
								<span v-if="!turnEntries.length" class="box-empty">// NO ACTIONS LOGGED //</span>
							</div>
						</div>
					</div>
				</div>

				<!-- Quick glance at every faction matching the current filters -->
				<div class="overview" v-else @click="clickOutside">
					<div class="overview-toolbar">
						<label class="search-field">
							<i class="mdi mdi-magnify search-icon" aria-hidden="true"></i>
							<input
								v-model="search"
								class="search-input"
								placeholder="Search name, type, tags, worlds..."
								type="text"
							/>
						</label>
						<!-- Point of view: the overview as one faction sees the others. Back to the
						     party view: click the faction again, click empty space, or press Esc -->
						<div class="pov-head" :class="{ shown: viewer }">
							<template v-if="viewer">
								<span class="stat-label">Viewing as</span>
								<span class="pov-name">{{ viewer.name }}</span>
								<span v-if="!pov" class="pov-party">// the party</span>
							</template>
						</div>
						<div class="pov-actions">
							<button v-if="viewer" class="filter-pill pov-dossier" @click="selectFaction(viewer)">
								Open dossier ▸
							</button>
						</div>
					</div>
					<div ref="overviewGrid" class="overview-grid">
						<!-- Where the party stands: allies and enemies by name, everyone else as a count -->
						<div ref="standing" class="overview-stat standing-panel">
							<span class="stat-label">{{
								pov ? "How they see the others" : "Party standing"
							}}</span>
							<div class="standing-sides">
								<div v-for="side in ['allied', 'hostile']" :key="side" class="standing-side">
									<div class="standing-side-head" :class="side">
										<span class="overview-value">{{ dispositionCounts[side] }}</span>
										<span>{{ side }}</span>
									</div>
									<div class="standing-chips">
										<button
											v-for="faction in byDisposition[side]"
											:key="faction.id"
											class="faction-chip"
											:class="side"
											@click="setPov(faction)"
										>
											<img v-if="faction.emblem" :src="faction.emblem" />
											<span v-else class="chip-emblem-empty"></span>
											{{ faction.name }}
										</button>
										<span v-if="!byDisposition[side].length" class="box-empty"
											>// NONE ON RECORD //</span
										>
									</div>
								</div>
							</div>
							<div class="standing-neutral">
								<span class="overview-value">{{ dispositionCounts.neutral }}</span>
								<span>neutral / unaligned</span>
							</div>
						</div>
						<div
							v-for="faction in filteredFactions"
							:key="faction.id"
							class="overview-card"
							:class="{ 'pov-self': viewer && viewer.id === faction.id }"
							:title="
								viewer && viewer.id === faction.id
									? pov
										? 'Back to the party view'
										: 'The party\'s company'
									: 'See the others through ' + faction.name + '\'s eyes'
							"
							@click="setPov(faction)"
						>
							<img v-if="faction.emblem" :src="faction.emblem" class="overview-emblem" />
							<span v-else class="overview-emblem overview-emblem-empty"></span>
							<div class="overview-info">
								<div class="overview-topline">
									<h2 v-marquee="'.overview-card'">{{ faction.name }}</h2>
									<span
										v-if="(!viewer || viewer.id !== faction.id) && side(faction) !== 'neutral'"
										class="status-badge"
										:class="side(faction)"
										>{{ side(faction) }}</span
									>
								</div>
								<span class="overview-motto">{{ faction.seat || noSeat }}</span>
								<!-- Disposition from -4 (hostile) to +4 (allied), drawn out from the centre -->
								<div class="overview-meter disposition-meter">
									<div
										class="overview-meter-fill"
										:class="side(faction)"
										:style="meterStyle(faction, standing(faction))"
									></div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="filteredFactions.length === 0" class="faction-list-empty">
						<p>// NO FACTIONS MATCH FILTER //</p>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import { VueMarkdownIt } from "@f3ve/vue-markdown-it";
import { factions, tagRule } from "@/data/factions.js";
import { withLiveFactions } from "@/data/liveState.js";
import FactionStatus from "@/components/FactionStatus.vue";
import { animate, stagger } from "motion";
import { revealRecord, retrieveRows, fillPips, countUp, reducedMotion } from "@/utils/retrieve";
import { onEscape } from "@/utils/escape";

const EASE_OUT = [0.22, 1, 0.36, 1];
// The party's company; its relations drive the default standings and dispositions
const PARTY_ID = "westwind-company";

export default {
	components: { FactionStatus, VueMarkdownIt },
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			selectedFaction: null,
			animateView: this.animate,
			animationDelay: "1.75s",
			search: "",
			pov: null, // faction whose point of view the overview shows (null: the party's)
			noSeat: "No holdings in the Dawnline Shore",
		};
	},
	computed: {
		// The Westwind Company's standings that Foundry keeps lay over its file's (src/data/liveState.js)
		factions() {
			return withLiveFactions(factions, PARTY_ID);
		},
		// The party's own company: its relations are the default standings
		party() {
			return this.factions.find(f => f.id === PARTY_ID) || null;
		},
		// Whose eyes the overview uses: the chosen faction, else the party
		viewer() {
			return this.pov || this.party;
		},
		filteredFactions() {
			const query = this.search.trim().toLowerCase();
			return this.factions.filter(faction => {
				if (query) {
					const haystack = [
						faction.name,
						faction.motto,
						faction.homeworld,
						faction.demonym,
						faction.politicalAlignment,
						faction.type,
						faction.subtype,
						faction.seat,
						...faction.tags,
						...faction.holdings,
					]
						.join(" ")
						.toLowerCase();
					if (!haystack.includes(query)) return false;
				}
				return true;
			});
		},
		filtersActive() {
			return this.search !== "";
		},
		// Overview standings, from the party's point of view or the chosen faction's
		others() {
			const viewer = this.viewer;
			return this.filteredFactions.filter(f => !viewer || f.id !== viewer.id);
		},
		byDisposition() {
			const sides = { allied: [], neutral: [], hostile: [] };
			this.others.forEach(f => sides[this.side(f)].push(f));
			// Strongest feelings first
			sides.allied.sort((a, b) => this.standing(b) - this.standing(a));
			sides.hostile.sort((a, b) => this.standing(a) - this.standing(b));
			return sides;
		},
		dispositionCounts() {
			const counts = { allied: 0, neutral: 0, hostile: 0 };
			this.others.forEach(f => counts[this.side(f)]++);
			return counts;
		},
		// The open faction's turns plus turns where another faction targeted it, by date
		// The open faction's relations, strongest first on each side
		dossierRelations() {
			const f = this.selectedFaction;
			const out = { allied: [], hostile: [] };
			if (!f) return out;
			Object.entries(f.relations).forEach(([id, v]) => {
				const faction = this.factionById(id);
				const value = Number(v) || 0;
				if (faction && value) out[value > 0 ? "allied" : "hostile"].push({ faction, value });
			});
			out.allied.sort((a, b) => b.value - a.value);
			out.hostile.sort((a, b) => a.value - b.value);
			return out;
		},
		turnEntries() {
			const f = this.selectedFaction;
			if (!f) return [];
			const own = f.turnLog.map(t => ({ ...t, by: null }));
			const incoming = this.factions
				.filter(o => o.id !== f.id)
				.flatMap(o => o.turnLog.filter(t => t.target === f.id).map(t => ({ ...t, by: o })));
			return [...own, ...incoming].sort((a, b) => a.turn.localeCompare(b.turn));
		},
	},
	mounted() {
		if (this.animate) this.introOverview();
		// Esc: close the dossier, then leave the point of view, then clear the search
		this.offEscape = onEscape(() => {
			if (this.selectedFaction) return (this.selectedFaction = null), true;
			if (this.pov) return this.setPov(null), true;
			if (this.search) return (this.search = ""), true;
			return false;
		});
	},
	beforeUnmount() {
		this.offEscape?.();
	},
	watch: {
		// Every dossier opening (and every return to the overview) plays its entrance
		selectedFaction(faction) {
			this.$nextTick(() => {
				if (!faction) {
					this.introOverview(0);
					return;
				}
				const dossier = this.$refs.dossier;
				if (!dossier) return;
				revealRecord(
					dossier.querySelector(".dossier-header"),
					dossier.querySelectorAll(
						".dossier-left > :not(.dossier-header), .dossier-banner-wrapper, .assets-holdings-row > *"
					)
				);
				this.loadDossier(dossier);
			});
		},
	},
	methods: {
		// Standing wipes in on its own, then its rows and chips, then the faction cards
		introOverview(base = 0.55) {
			const standing = this.$refs.standing;
			const grid = this.$refs.overviewGrid;
			if (!standing || !grid || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
				return;
			animate(
				standing,
				{ clipPath: ["inset(0 100% 0 0)", "inset(0 0% 0 0)"], opacity: [0, 1] },
				{ duration: 0.9, delay: base, ease: EASE_OUT }
			).finished.then(() => setTimeout(() => (standing.style.clipPath = ""), 50));
			animate(
				standing.querySelectorAll(".stat-label, .standing-side, .standing-neutral"),
				{ opacity: [0, 1], x: [-14, 0] },
				{ duration: 0.5, delay: stagger(0.12, { startDelay: base + 0.4 }), ease: EASE_OUT }
			);
			const chips = standing.querySelectorAll(".faction-chip");
			if (chips.length)
				animate(
					chips,
					{ opacity: [0, 1], scale: [0.85, 1] },
					{ duration: 0.35, delay: stagger(0.05, { startDelay: base + 0.75 }), ease: EASE_OUT }
				);
			animate(
				grid.querySelectorAll(".overview-card"),
				{ opacity: [0, 1], y: [12, 0] },
				{ duration: 0.45, delay: stagger(0.025, { startDelay: base + 0.2 }), ease: EASE_OUT }
			);
		},
		// The dossier's gauges and lists load in after the record reveal: ratings count up and
		// fill their boxes, HP draws across, goal progress lights, holdings and turns wipe in
		loadDossier(dossier) {
			const q = sel => dossier.querySelectorAll(sel);
			q(".swn-attr").forEach((attr, i) => {
				const value = attr.querySelector(".swn-attr-value");
				countUp(value, Number(value.textContent), { delay: 0.35 + i * 0.12 });
				fillPips(attr.querySelectorAll(".attr-box.filled"), { delay: 0.4 + i * 0.12, gap: 0.05 });
			});
			const hp = dossier.querySelector(".hp-fill");
			if (hp && !reducedMotion())
				animate(hp, { scaleX: [0, 1] }, { duration: 0.8, delay: 0.55, ease: EASE_OUT });
			fillPips(q(".goal-box.filled"), { delay: 0.75 });
			retrieveRows(q(".holding-section, .holding-row"), { startDelay: 0.45, gap: 0.04 });
			retrieveRows(q(".turn-row"), { startDelay: 0.55, gap: 0.08 });
			const chips = q(".dossier-relations .faction-chip");
			if (chips.length && !reducedMotion())
				animate(
					chips,
					{ opacity: [0, 1], scale: [0.85, 1] },
					{ duration: 0.3, delay: stagger(0.03, { startDelay: 0.5 }), ease: EASE_OUT }
				);
		},
		selectFaction(faction) {
			// Clicking the open dossier's entry again returns to the overview
			this.selectedFaction =
				this.selectedFaction && this.selectedFaction.id === faction.id ? null : faction;
		},
		disposition(faction) {
			if (faction.status < 0) return "hostile";
			if (faction.status > 0) return "allied";
			return "neutral";
		},
		// The party's standing with a faction (its company's relation to it)
		partyStanding(faction) {
			if (!this.party) return faction.status;
			return Number(this.party.relations[faction.id]) || 0;
		},
		meterStyle(faction, value = faction.status) {
			const pct = Math.min(Math.abs(value), 4) * 12.5;
			return { left: value < 0 ? `${50 - pct}%` : "50%", width: `${pct}%` };
		},
		// How the current point of view feels about a faction (-4..+4)
		standing(faction) {
			if (!this.viewer) return faction.status;
			return Number(this.viewer.relations[faction.id]) || 0;
		},
		side(faction) {
			const v = this.standing(faction);
			return v < 0 ? "hostile" : v > 0 ? "allied" : "neutral";
		},
		// Clicking an overview card shows the overview through that faction's eyes;
		// clicking it again (or "Party view") returns to the party's
		// Clicking anywhere that isn't a card or a control returns to the party view
		clickOutside(e) {
			if (this.pov && !e.target.closest(".overview-card, button, input, a, .faction-chip"))
				this.setPov(null);
		},
		setPov(faction) {
			// The party's own card always returns to the party view
			const fresh = faction && faction.id !== PARTY_ID && (!this.pov || this.pov.id !== faction.id);
			this.pov = fresh ? faction : null;
			this.$nextTick(() => this.refreshStanding());
		},
		// Changing point of view only refreshes what depends on it: the Standing panel's
		// rows and chips, and each card's relationship bar and badge. The cards stay put.
		refreshStanding() {
			const standing = this.$refs.standing;
			const grid = this.$refs.overviewGrid;
			if (!standing || !grid || reducedMotion()) return;
			animate(
				standing.querySelectorAll(".standing-side-head, .standing-neutral"),
				{ opacity: [0, 1], x: [-14, 0] },
				{ duration: 0.4, delay: stagger(0.08), ease: EASE_OUT }
			);
			const chips = standing.querySelectorAll(".faction-chip");
			if (chips.length)
				animate(
					chips,
					{ opacity: [0, 1], scale: [0.85, 1] },
					{ duration: 0.3, delay: stagger(0.03, { startDelay: 0.15 }), ease: EASE_OUT }
				);
			animate(
				grid.querySelectorAll(".overview-meter-fill"),
				{ scaleX: [0, 1] },
				{ duration: 0.5, delay: stagger(0.015), ease: EASE_OUT }
			);
			const badges = grid.querySelectorAll(".overview-card .status-badge");
			if (badges.length) animate(badges, { opacity: [0, 1] }, { duration: 0.3, delay: 0.25 });
		},
		tagRule,
		factionById(id) {
			return this.factions.find(f => f.id === id);
		},
		// Hover card for an asset named in the turn log: its SWN stats and rules text
		assetTip(asset) {
			const stats = [
				asset.category && asset.category[0].toUpperCase() + asset.category.slice(1),
				asset.rating && `Rating ${asset.rating}`,
				asset.hp && `HP ${asset.hp}`,
				asset.cost != null && `Cost ${asset.cost} Cred`,
				asset.attack && `Attack ${asset.attack}`,
				asset.counter && `Counter ${asset.counter}`,
			].filter(Boolean);
			return {
				title: asset.name,
				body: [stats.join(" · "), asset.description].filter(Boolean).join("\n\n"),
			};
		},
		clearFilters() {
			this.search = "";
		},
	},
};
</script>

<style scoped>
/* ── Single full-width panel ── */
/* Sits in the gap above the panel, where it used to between the two columns */
.factions-source {
	position: absolute;
	top: 24px;
	left: 0;
	right: 0;
	text-align: center;
	pointer-events: none;
}

#factionsView {
	position: relative;
}

.overview-toolbar {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
	align-items: center;
	gap: 0.8em 1.6em;
	padding: 1.5em 1.5em 0;
	min-height: 36px;
}

.overview-toolbar .search-field {
	width: 340px;
	max-width: 100%;
}

.overview-toolbar .search-input {
	height: 34px;
	font-size: var(--fs-mono);
}

.overview-toolbar .filter-group {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.7em;
}

.overview-toolbar .filter-pill {
	padding: 0.4em 0.9em;
	font-size: var(--fs-mono-s);
}

/* Original card size, in wider columns (as on The Milieu) so names fit */
.overview-grid {
	grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
	gap: 0.9em;
}

.overview-card {
	align-items: center;
}

/* ── Gap tag ── */
.faction-list-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 200px;
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono-s);
	letter-spacing: 0.1em;
	color: var(--text-pilot-code);
	opacity: 0.4;
}

/* ── Disposition colours (list badges, overview figures, meters) ── */
/* Relationship bars grow out from the centre line when the point of view changes */
.overview-meter-fill.allied {
	transform-origin: left center;
}
.overview-meter-fill.hostile {
	transform-origin: right center;
}

.status-badge.allied,
.overview-figure.allied,
.overview-meter-fill.allied {
	color: #3b7a57;
}
.status-badge.hostile,
.overview-figure.hostile,
.overview-meter-fill.hostile {
	color: #c0394f;
}
.overview-figure.neutral {
	color: var(--text-pilot-value);
}

/* ── Overview cards ── */
.overview-emblem {
	flex-shrink: 0;
	width: 56px;
	height: 56px;
	border-radius: 50%;
}

/* Factions without an emblem (Westwind) get an empty dashed ring */
.overview-emblem-empty {
	box-sizing: border-box;
	border: 1px dashed rgba(184, 151, 58, 0.45);
}

/* ── Standing block: first tile of the card grid, cards wrap around it ── */
.overview {
	container-type: inline-size;
}

/* Height 0 + min-height 100%: the panel fills the rows it spans but never makes them
   taller, so cards stay one size however many factions are listed; it scrolls instead */
.standing-panel.overview-stat {
	height: 0;
	min-height: 100%;
	overflow-y: auto;
}

.standing-panel.overview-stat {
	grid-column: span 2;
	grid-row: span 4;
	justify-content: flex-start;
	gap: 0.8em;
}

@container (max-width: 620px) {
	.standing-panel.overview-stat {
		grid-column: 1 / -1;
		grid-row: auto;
		height: auto;
		min-height: 0;
		overflow: visible;
	}
}

/* ── Point of view: centred in the search row, dossier on the right ── */
.pov-head {
	display: flex;
	align-items: baseline;
	justify-content: center;
	gap: 0.7em;
	min-width: 0;
}

.pov-name {
	font-family: "Big Shoulders Display", cursive;
	font-size: var(--fs-display-l);
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--text-mission-status);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.pov-party {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono-s);
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--text-pilot-code);
}

.pov-actions {
	display: flex;
	justify-content: flex-end;
}

/* Restate the full border: the global ":last-child" rule strips the bottom edge */
.overview-toolbar .pov-dossier,
.overview-toolbar .pov-dossier:last-child {
	padding: 0.55em 1.2em;
	font-size: var(--fs-chip);
	border: 1px solid #b8973a;
	color: #b8973a;
}

.overview-toolbar .pov-dossier:hover {
	background: rgba(184, 151, 58, 0.12);
}

.overview-card.pov-self {
	border-color: #b8973a;
	background-color: rgba(184, 151, 58, 0.1);
}

/* Allied and hostile side by side in the wide panel; stacked when it's narrow */
.standing-sides {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(18em, 1fr));
	gap: 0.8em 1.5em;
	align-items: start;
}

.standing-side-head,
.standing-neutral {
	display: flex;
	align-items: baseline;
	gap: 0.4em;
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-chip);
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: var(--text-pilot-value);
}

.standing-side-head .overview-value,
.standing-neutral .overview-value {
	font-size: var(--fs-display-xl);
}

.standing-side-head.allied {
	color: #3b7a57;
}
.standing-side-head.hostile {
	color: #c0394f;
}

.standing-neutral {
	margin-top: auto;
	padding-top: 0.6em;
	border-top: 1px solid rgba(209, 200, 184, 0.1);
}

.standing-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 0.35em;
	margin-top: 0.3em;
}

.standing-chips .box-empty {
	width: 100%;
	margin: 0;
	padding: 0;
	text-align: center;
}

.faction-chip {
	display: inline-flex;
	align-items: center;
	gap: 0.4em;
	padding: 0.15em 0.6em 0.15em 0.2em;
	border: 1px solid rgba(209, 200, 184, 0.2);
	border-radius: 999px;
	background: rgba(26, 26, 31, 0.5);
	font-family: "Big Shoulders Display", cursive;
	font-size: var(--fs-display-s);
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--text-mission-status);
	cursor: pointer;
}

.faction-chip.allied {
	border-color: rgba(59, 122, 87, 0.7);
}
.faction-chip.hostile {
	border-color: rgba(192, 57, 79, 0.7);
}
.faction-chip:hover {
	border-color: #b8973a;
}

.faction-chip img,
.chip-emblem-empty {
	display: block;
	width: 22px;
	height: 22px;
	border-radius: 50%;
}

.chip-emblem-empty {
	border: 1px dashed rgba(184, 151, 58, 0.4);
	box-sizing: border-box;
}

.overview-motto {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono-s);
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--text-mission-status);
}

/* Centre tick marks neutral; the fill grows left (hostile) or right (allied) */
.disposition-meter {
	position: relative;
	margin-top: auto;
}

.disposition-meter::after {
	content: "";
	position: absolute;
	left: 50%;
	top: -2px;
	width: 1px;
	height: 7px;
	background: rgba(209, 200, 184, 0.4);
}

.disposition-meter .overview-meter-fill {
	position: absolute;
	top: 0;
}

/* ── Dossier ── */
.faction-dossier {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: auto;
}

.dossier-top {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: 1.5em;
	padding: 1.5em;
}

.dossier-left {
	display: flex;
	flex-direction: column;
	flex: 1;
	gap: 1em;
}

.dossier-banner-wrapper {
	flex-shrink: 0;
	align-self: flex-start;
}

.dossier-banner {
	width: 240px;
	height: 300px;
	object-fit: contain;
	border: 1px solid var(--primary-color);
	padding: 1.5em;
	background: rgba(26, 26, 31, 0.5);
}

.dossier-emblem {
	width: 200px;
	height: 200px;
	border-radius: 50%;
}

/* Labels inside the dossier use the label size (the global .stat-label is 9px) */
.faction-dossier .stat-label {
	font-size: var(--fs-label);
}

/* ── Stats ── */
.stat-section {
	display: flex;
	flex-direction: column;
	gap: 0.4em;
}

/* ── Assets & Holdings ── */
.assets-holdings-row {
	display: flex;
	gap: 1em;
	padding: 0 1.5em 1.5em;
	flex: 1;
	min-height: 0;
}

.assets-box {
	flex: 1;
	border: 1px solid var(--primary-color);
	display: flex;
	flex-direction: column;
}

.box-header {
	padding: 0.4em 0.6em;
	background: var(--primary-color);
}

.box-content {
	padding: 0.5em;
	display: flex;
	flex-wrap: wrap;
	gap: 0.4em;
	flex: 1;
	align-content: flex-start;
	align-items: flex-start;
}

.box-empty {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono-s);
	letter-spacing: 0.1em;
	color: var(--text-pilot-code);
	opacity: 0.4;
	align-self: center;
	width: 100%;
	text-align: center;
}

.asset-tag {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-chip);
	letter-spacing: 0.05em;
	padding: 0.3em 0.7em;
	border: 1px solid var(--primary-color);
	color: var(--text-pilot-code);
	text-transform: uppercase;
}

/* Holdings | Turn log: holdings take the larger share */
.holdings-box {
	flex: 2;
	min-width: 0;
}

.turnlog-box {
	flex: 1;
	min-width: 0;
}

/* Stat card left, description right; stacks when narrow */
.profile-split {
	display: grid;
	grid-template-columns: minmax(0, 1.7fr) minmax(16em, 1fr);
	gap: 1.5em;
	align-items: start;
	margin-bottom: 1em;
}

.profile-split > .profile {
	margin-bottom: 0;
}

.profile-split > :only-child {
	grid-column: 1 / -1;
}

@media (max-width: 1100px) {
	.profile-split {
		grid-template-columns: 1fr;
	}
}

/* ── Dossier relations: chips under the description ── */
.dossier-relations {
	display: flex;
	flex-direction: column;
	gap: 0.6em;
	margin-top: 1.2em;
}

.relation-group {
	display: flex;
	flex-direction: column;
	gap: 0.35em;
}

.relation-side {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono-s);
	letter-spacing: 0.1em;
	text-transform: uppercase;
}

.relation-side.allied,
.relation-value.allied {
	color: #3b7a57;
}
.relation-side.hostile,
.relation-value.hostile {
	color: #c0394f;
}

.relation-value {
	margin-left: 0.2em;
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
}

/* ── Faction profile (SWN stats, goal, tags): follows the Status pilot panel ── */
.profile {
	display: flex;
	flex-direction: column;
	gap: 0.8em;
	margin-bottom: 1.2em;
	padding: 1em 1.2em;
	border: 1px solid rgba(184, 151, 58, 0.25);
	background: rgba(26, 26, 31, 0.35);
}

.profile-attrs {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.7em;
}

.swn-attr {
	display: grid;
	grid-template-columns: auto 1fr;
	grid-template-areas: "value name" "boxes boxes";
	align-items: baseline;
	column-gap: 0.5em;
	row-gap: 0.35em;
	padding: 0.5em 0.7em 0.6em;
	border: 1px solid currentColor;
}

.swn-attr.force {
	color: #c0394f;
}
.swn-attr.cunning {
	color: #5b8fd9;
}
.swn-attr.wealth {
	color: #b8973a;
}

.swn-attr-value {
	grid-area: value;
	font-family: "Big Shoulders Display", cursive;
	font-size: var(--fs-display-xl);
	font-weight: 800;
	line-height: 1;
}

.swn-attr-name {
	grid-area: name;
	font-family: "Raleway", sans-serif;
	font-size: var(--fs-label);
	font-weight: 800;
	letter-spacing: 3px;
	text-transform: uppercase;
}

.attr-boxes {
	grid-area: boxes;
	display: flex;
	gap: 3px;
}

.attr-box,
.goal-box {
	flex: 1;
	height: 10px;
	border: 1px solid currentColor;
	opacity: 0.35;
	transform: skewX(-12deg);
}

.attr-box.filled {
	background: currentColor;
	opacity: 1;
}

.profile-row {
	display: flex;
	align-items: center;
	gap: 0.75em;
}

.profile-top {
	align-items: flex-start;
}

.profile-label {
	flex: 0 0 4.5em;
	font-family: "Raleway", sans-serif;
	font-size: var(--fs-label);
	font-weight: 800;
	letter-spacing: 3px;
	color: var(--text-location);
}

.profile-top > .profile-label {
	padding-top: 0.35em;
}

.profile-label-inline {
	flex-basis: auto;
	margin-left: 2em;
}

.profile-value {
	font-family: "Big Shoulders Display", cursive;
	font-size: var(--fs-display-m);
	font-weight: 800;
	color: var(--text-mission-status);
}

.profile-of {
	font-family: "Big Shoulders Display", cursive;
	font-size: var(--fs-display-s);
	font-weight: 800;
	color: var(--text-pilot-code);
}

.hp-track {
	flex: 1;
	height: 14px;
	border: 1px solid rgba(209, 200, 184, 0.35);
	background: rgba(26, 26, 31, 0.5);
	transform: skewX(-12deg);
	overflow: hidden;
}

.hp-fill {
	display: block;
	transform-origin: left center;
	height: 100%;
	background: #3b7a57;
}

.profile-goal,
.profile-tags {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.4em;
}

.goal-head {
	display: flex;
	align-items: baseline;
	gap: 0.6em;
}

.goal-name {
	font-family: "Big Shoulders Display", cursive;
	font-size: var(--fs-display-m);
	font-weight: 800;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: #b8973a;
}

.goal-target {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
	color: var(--text-pilot-code);
}

.goal-progress {
	display: flex;
	align-items: center;
	gap: 4px;
	max-width: 22em;
	color: #b8973a;
}

.goal-progress .profile-of {
	margin-left: 0.6em;
}

.goal-box.filled {
	background: currentColor;
	opacity: 1;
}

/* Several tags sit side by side instead of one long column */
.profile-tags {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(17em, 1fr));
	gap: 0.7em 1.4em;
}

.tag-name {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: #b8973a;
}

.profile-rule {
	margin: 0.1em 0 0;
	font-family: "Titillium Web", cursive;
	font-size: var(--fs-body-s);
	line-height: 1.4;
	color: var(--text-markdown-p);
	opacity: 0.85;
}

@media (max-width: 760px) {
	.profile-attrs {
		grid-template-columns: 1fr;
	}
}

.holdings-box,
.turnlog-box {
	min-height: 0;
}

.holdings-content,
.turnlog-content {
	min-height: 0;
	overflow-y: auto;
	flex-direction: column;
	flex-wrap: nowrap;
	align-items: stretch;
	gap: 0.8em;
	padding: 0.7em 0.8em;
}

.holding-row,
.holding-row summary {
	display: grid;
	grid-template-columns: var(--icon-badge) minmax(0, 1fr) minmax(0, 14em) 6.5em;
	align-items: baseline;
	column-gap: 0.7em;
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
	letter-spacing: 0.05em;
	color: var(--text-pilot-value);
}

details.holding-row {
	display: block;
}

.holding-row summary {
	cursor: pointer;
	list-style: none;
}

.holding-row summary::-webkit-details-marker {
	display: none;
}

.holding-row summary:hover .holding-name {
	color: #b8973a;
}

.asset-row[open] summary .holding-name {
	color: #b8973a;
}

.holding-cat {
	font-weight: bold;
	text-transform: uppercase;
	text-align: center;
	border: 1px solid currentColor;
	font-size: var(--fs-mono);
	line-height: calc(var(--icon-badge) - 2px);
}

.holding-cat.force {
	color: #c0394f;
}
.holding-cat.cunning {
	color: #5b8fd9;
}
.holding-cat.wealth {
	color: #b8973a;
}

.holding-name {
	text-transform: uppercase;
	overflow-wrap: anywhere;
}

.base-row .holding-name {
	color: var(--text-mission-status);
}

.holding-stat {
	color: var(--text-pilot-code);
	white-space: nowrap;
}

.holding-section {
	font-family: "Big Shoulders Display", cursive;
	font-size: var(--fs-display-m);
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--text-mission-status);
	border-bottom: 1px solid rgba(184, 151, 58, 0.25);
	padding-bottom: 0.15em;
	margin-top: 0.4em;
}

.holding-section:first-child {
	margin-top: 0;
}

.holding-loc {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
	color: var(--text-pilot-code);
	overflow-wrap: anywhere;
}

.holding-stat {
	text-align: right;
}

.homeworld-tag {
	margin-left: 0.7em;
	padding: 0.1em 0.5em;
	border: 1px solid #b8973a;
	color: #b8973a;
	font-size: var(--fs-mono-s);
	vertical-align: middle;
}

.asset-specs .asset-tag.force {
	border-color: #c0394f;
	color: #c0394f;
}
.asset-specs .asset-tag.cunning {
	border-color: #5b8fd9;
	color: #5b8fd9;
}
.asset-specs .asset-tag.wealth {
	border-color: #b8973a;
	color: #b8973a;
}

.holding-note {
	grid-column: 2 / -1;
	font-size: var(--fs-body-s);
	color: var(--text-pilot-code);
	opacity: 0.75;
	text-transform: none;
	font-family: "Titillium Web", cursive;
	letter-spacing: normal;
}

.asset-detail {
	margin: 0.3em 0 0.6em calc(var(--icon-badge) + 0.7em);
	padding-left: 0.6em;
	border-left: 1px solid rgba(184, 151, 58, 0.35);
}

.asset-specs {
	display: flex;
	flex-wrap: wrap;
	gap: 0.4em;
}

.asset-detail p {
	margin: 0.4em 0 0;
	font-family: "Titillium Web", cursive;
	font-size: var(--fs-body-s);
	color: var(--text-markdown-p);
}

.asset-specs .asset-tag {
	font-size: var(--fs-chip);
}

.turn-row {
	display: flex;
	flex-direction: column;
	gap: 0.35em;
	padding-bottom: 0.7em;
	border-bottom: 1px solid rgba(184, 151, 58, 0.1);
}

.turn-head {
	display: flex;
	align-items: baseline;
	gap: 0.7em;
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
	letter-spacing: 0.05em;
}

.turn-when {
	color: #b8973a;
}

.turn-action {
	color: var(--text-pilot-value);
	text-transform: uppercase;
}

.turn-outcome {
	margin-left: auto;
	padding: 0.1em 0.5em;
	border: 1px solid currentColor;
	font-size: var(--fs-mono-s);
	text-transform: uppercase;
}

.turn-outcome.success {
	color: #3b7a57;
}
.turn-outcome.failure {
	color: #c0394f;
}

.turn-line,
.turn-result {
	margin: 0;
	font-family: "Titillium Web", cursive;
	font-size: var(--fs-body-s);
	line-height: 1.45;
	color: var(--text-markdown-p);
}

.turn-result {
	opacity: 0.8;
}

/* Assets in a turn: coloured by category, hover for stats */
.turn-asset {
	font-family: "Share Tech Mono", monospace;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	border-bottom: 1px dotted currentColor;
}

.turn-asset.force {
	color: #c0394f;
}
.turn-asset.cunning {
	color: #5b8fd9;
}
.turn-asset.wealth {
	color: #b8973a;
}

/* Factions in a turn open their dossier */
.turn-faction {
	padding: 0;
	border: none;
	background: none;
	font: inherit;
	color: #b8973a;
	text-decoration: underline;
	text-underline-offset: 2px;
	cursor: pointer;
}

.turn-faction:hover {
	color: var(--text-mission-status);
}

.turn-world {
	font-family: "Share Tech Mono", monospace;
	color: var(--text-pilot-value);
}

/* Never squeezed shut: below this the dossier scrolls instead */
.assets-holdings-row {
	min-height: 440px;
}

/* Phones: stack the boxes and let them grow, the dossier scrolls */
@media (max-width: 760px) {
	.assets-holdings-row {
		flex-direction: column;
		flex: none;
	}
	.holdings-content,
	.turnlog-content {
		overflow: visible;
	}
	.holding-row,
	.holding-row summary {
		grid-template-columns: var(--icon-badge) minmax(0, 1fr) auto;
	}
	.holding-loc {
		grid-column: 2 / -1;
		grid-row: 2;
	}
}

.dossier-quote {
	margin: 0 0 1em;
	padding-left: 1em;
	border-left: 2px solid var(--primary-color);
	font-family: "Titillium Web", cursive;
	font-style: italic;
	font-size: var(--fs-body-s);
	color: #999696;
}

/* ── Faction Info Grid ── */
.faction-info-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1em;
	margin-bottom: 1em;
}

.info-field {
	display: flex;
	flex-direction: column;
	gap: 0.3em;
}

.info-value.no-seat {
	opacity: 0.55;
}

.info-value {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
	color: var(--text-pilot-value);
	letter-spacing: 0.05em;
}

/* ── Significant Details ── */
.faction-details-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1em;
	margin-bottom: 1em;
}

.detail-field {
	display: flex;
	flex-direction: column;
	gap: 0.3em;
}

.detail-list {
	list-style: none;
	padding: 0;
	margin: 0;
}

.detail-list li {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono);
	color: var(--text-pilot-code);
	letter-spacing: 0.05em;
	padding: 0.2em 0;
	border-bottom: 1px solid rgba(184, 151, 58, 0.1);
}

.detail-list li:last-child {
	border-bottom: none;
}

.faction-note {
	font-family: "Titillium Web", cursive;
	font-size: var(--fs-body);
	color: var(--text-markdown-p);
}

/* Links in descriptions: gold like the rest of the site, not the browser blue */
.faction-note :deep(a) {
	color: #b8973a;
	text-decoration-color: rgba(184, 151, 58, 0.4);
	text-underline-offset: 2px;
}

.faction-note :deep(a:hover) {
	color: var(--text-mission-status);
}

/* The "Source: …" line under a quoted description: small and quiet */
.faction-note :deep(p:has(> em:only-child)) {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono-s);
	letter-spacing: 0.05em;
	color: var(--text-pilot-code);
}

.faction-note :deep(p:has(> em:only-child) em) {
	font-style: normal;
}

.faction-note :deep(p) {
	margin: 0 0 1em;
}

.faction-note :deep(blockquote) {
	margin: 0 0 1em;
	padding-left: 1em;
	border-left: 2px solid #b8973a;
	color: #b8973a;
}

.faction-missing {
	font-family: "Share Tech Mono", monospace;
	font-size: var(--fs-mono-s);
	letter-spacing: 0.1em;
	color: var(--text-pilot-code);
	opacity: 0.5;
}
</style>
